import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Save, X, Plus, Trash, Sparkles, Lightbulb } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface QuestionData {
  id: string;
  text: string;
  type: string;
  explanation?: string;
  difficulty?: string;
  options?: Array<{ text: string; is_correct: boolean }>;
  pairs?: Array<{ left: string; right: string }>;
  puzzle_data?: any;
  blanks?: string[];
  correct_answer?: boolean;
}

interface EnhancedQuestionEditorProps {
  question: QuestionData;
  testData: {
    subject: string;
    grade: string;
    topic: string;
  };
  isOpen: boolean;
  onSave: (questionData: any) => void;
  onCancel: () => void;
}

export default function EnhancedQuestionEditor({ 
  question, 
  testData, 
  isOpen, 
  onSave, 
  onCancel 
}: EnhancedQuestionEditorProps) {
  const [questionText, setQuestionText] = useState(question.text);
  const [questionType, setQuestionType] = useState(question.type || 'multiple_choice');
  const [explanation, setExplanation] = useState(question.explanation || '');
  const [difficulty, setDifficulty] = useState(question.difficulty || 'medium');
  const [touched, setTouched] = useState(false);

  // Multiple choice state
  const [mcOptions, setMcOptions] = useState<Array<{ text: string; is_correct: boolean }>>(
    question.options || [
      { text: "", is_correct: true },
      { text: "", is_correct: false }
    ]
  );

  // Matching pairs state
  const [matchingPairs, setMatchingPairs] = useState(
    question.pairs || [
      { left: "", right: "" },
      { left: "", right: "" }
    ]
  );

  // True/False state
  const [trueFalseAnswer, setTrueFalseAnswer] = useState<boolean>(question.correct_answer !== undefined ? question.correct_answer : true);

  // Fill in blank state
  const [fillBlanks, setFillBlanks] = useState(question.blanks || [""]);
  const [fillQuestion, setFillQuestion] = useState(questionText);

  // AI suggestions state
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);

  useEffect(() => {
    setQuestionText(question.text);
    setQuestionType(question.type || 'multiple_choice');
    setExplanation(question.explanation || '');
  }, [question]);

  const getSuggestions = async () => {
    setLoadingSuggestions(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-ai-questions', {
        body: {
          topic: testData.topic,
          grade: testData.grade,
          subject: testData.subject,
          questionTypes: [questionType],
          numberOfQuestions: 3,
          difficulty,
          additionalContext: `Provide suggestions to improve this question: "${questionText}". Focus on clarity, engagement, and educational value.`
        }
      });

      if (error) throw error;
      setSuggestions(data.questions.map((q: any) => q.question));
      toast.success('Generated suggestions!');
    } catch (error) {
      console.error('Error getting suggestions:', error);
      toast.error('Failed to get AI suggestions');
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const handleSave = async () => {
    // Validate content for inappropriate words
    const { validateContent } = await import('@/utils/contentValidation');
    
    const questionValidation = validateContent(questionText);
    if (!questionValidation.isValid) {
      toast.error(`Question: ${questionValidation.message}`);
      return;
    }

    if (explanation.trim()) {
      const explanationValidation = validateContent(explanation);
      if (!explanationValidation.isValid) {
        toast.error(`Explanation: ${explanationValidation.message}`);
        return;
      }
    }

    const questionData: any = {
      id: question.id,
      text: questionText.trim(),
      type: questionType,
      explanation: explanation.trim() || null,
      difficulty
    };

    switch (questionType) {
      case 'multiple_choice':
        if (mcOptions.some(opt => !opt.text.trim())) {
          toast.error('All options must have text');
          return;
        }
        if (!mcOptions.some(opt => opt.is_correct)) {
          toast.error('At least one option must be correct');
          return;
        }
        // Validate option content
        for (const option of mcOptions) {
          const optionValidation = validateContent(option.text);
          if (!optionValidation.isValid) {
            toast.error(`Option content: ${optionValidation.message}`);
            return;
          }
        }
        questionData.options = mcOptions.map(opt => ({
          text: opt.text.trim(),
          is_correct: opt.is_correct
        }));
        break;

      case 'matching':
        if (matchingPairs.some(pair => !pair.left.trim() || !pair.right.trim())) {
          toast.error('All pairs must have both terms and definitions');
          return;
        }
        // Validate matching pairs content
        for (const pair of matchingPairs) {
          const leftValidation = validateContent(pair.left);
          if (!leftValidation.isValid) {
            toast.error(`Matching term: ${leftValidation.message}`);
            return;
          }
          const rightValidation = validateContent(pair.right);
          if (!rightValidation.isValid) {
            toast.error(`Matching definition: ${rightValidation.message}`);
            return;
          }
        }
        questionData.pairs = matchingPairs.map(pair => ({
          left: pair.left.trim(),
          right: pair.right.trim()
        }));
        break;

      case 'true_false':
        questionData.correct_answer = trueFalseAnswer;
        break;

      case 'fill_blank':
        if (fillBlanks.some(blank => !blank.trim())) {
          toast.error('All blanks must have answers');
          return;
        }
        // Validate fill-in-the-blank content
        for (const blank of fillBlanks) {
          const blankValidation = validateContent(blank);
          if (!blankValidation.isValid) {
            toast.error(`Fill-in-the-blank answer: ${blankValidation.message}`);
            return;
          }
        }
        const fillQuestionValidation = validateContent(fillQuestion);
        if (!fillQuestionValidation.isValid) {
          toast.error(`Fill-in-the-blank question: ${fillQuestionValidation.message}`);
          return;
        }
        questionData.blanks = fillBlanks.map(blank => blank.trim());
        questionData.text = fillQuestion;
        break;
    }

    onSave(questionData);
  };

  const addMcOption = () => {
    setMcOptions([...mcOptions, { text: "", is_correct: false }]);
    setTouched(true);
  };

  const removeMcOption = (index: number) => {
    if (mcOptions.length <= 2) return;
    setMcOptions(mcOptions.filter((_, i) => i !== index));
    setTouched(true);
  };

  const updateMcOption = (index: number, field: 'text' | 'is_correct', value: string | boolean) => {
    const updated = mcOptions.map((opt, i) => 
      i === index 
        ? { ...opt, [field]: value }
        : field === 'is_correct' && value === true
          ? { ...opt, is_correct: false }
          : opt
    );
    setMcOptions(updated);
    setTouched(true);
  };

  const addMatchingPair = () => {
    setMatchingPairs([...matchingPairs, { left: "", right: "" }]);
    setTouched(true);
  };

  const removeMatchingPair = (index: number) => {
    if (matchingPairs.length <= 2) return;
    setMatchingPairs(matchingPairs.filter((_, i) => i !== index));
    setTouched(true);
  };

  const updateMatchingPair = (index: number, field: 'left' | 'right', value: string) => {
    const updated = matchingPairs.map((pair, i) => 
      i === index ? { ...pair, [field]: value } : pair
    );
    setMatchingPairs(updated);
    setTouched(true);
  };

  const addFillBlank = () => {
    setFillBlanks([...fillBlanks, ""]);
    setTouched(true);
  };

  const removeFillBlank = (index: number) => {
    if (fillBlanks.length <= 1) return;
    setFillBlanks(fillBlanks.filter((_, i) => i !== index));
    setTouched(true);
  };

  const updateFillBlank = (index: number, value: string) => {
    const updated = fillBlanks.map((blank, i) => i === index ? value : blank);
    setFillBlanks(updated);
    setTouched(true);
  };

  const renderQuestionTypeEditor = () => {
    switch (questionType) {
      case 'multiple_choice':
        return (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <Label>Answer Options</Label>
              <Button type="button" onClick={addMcOption} variant="outline" size="sm">
                <Plus className="h-4 w-4 mr-1" /> Add Option
              </Button>
            </div>
            <RadioGroup className="space-y-3">
              {mcOptions.map((option, index) => (
                <div key={index} className="flex items-start space-x-3 border p-3 rounded">
                  <RadioGroupItem
                    value={index.toString()}
                    checked={option.is_correct}
                    onClick={() => updateMcOption(index, 'is_correct', true)}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <Label className="text-sm font-medium">
                      Option {String.fromCharCode(65 + index)}
                      {option.is_correct && " (Correct)"}
                    </Label>
                    <Input
                      placeholder={`Enter option ${String.fromCharCode(65 + index)}`}
                      value={option.text}
                      onChange={(e) => updateMcOption(index, 'text', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeMcOption(index)}
                    disabled={mcOptions.length <= 2}
                    className="mt-1"
                  >
                    <Trash className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </RadioGroup>
          </div>
        );

      case 'matching':
        return (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <Label>Matching Pairs</Label>
              <Button type="button" onClick={addMatchingPair} variant="outline" size="sm">
                <Plus className="h-4 w-4 mr-1" /> Add Pair
              </Button>
            </div>
            <div className="space-y-3">
              {matchingPairs.map((pair, index) => (
                <div key={index} className="grid grid-cols-2 gap-3 p-3 border rounded">
                  <div>
                    <Label className="text-sm">Term {index + 1}</Label>
                    <Input
                      placeholder="Enter term"
                      value={pair.left}
                      onChange={(e) => updateMatchingPair(index, 'left', e.target.value)}
                    />
                  </div>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <Label className="text-sm">Definition {index + 1}</Label>
                      <Input
                        placeholder="Enter definition"
                        value={pair.right}
                        onChange={(e) => updateMatchingPair(index, 'right', e.target.value)}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeMatchingPair(index)}
                      disabled={matchingPairs.length <= 2}
                      className="mt-5"
                    >
                      <Trash className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'true_false':
        return (
          <div className="space-y-4">
            <Label>Correct Answer</Label>
            <RadioGroup
              value={trueFalseAnswer.toString()}
              onValueChange={(value) => {
                setTrueFalseAnswer(value === 'true');
                setTouched(true);
              }}
              className="flex gap-6"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="true" id="true" />
                <Label htmlFor="true">True</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="false" id="false" />
                <Label htmlFor="false">False</Label>
              </div>
            </RadioGroup>
          </div>
        );

      case 'fill_blank':
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Question with Blanks (Use ___ for blanks)</Label>
              <Textarea
                placeholder="Enter question with blanks, e.g., 'The ___ is the largest planet in our solar system.'"
                value={fillQuestion}
                onChange={(e) => {
                  setFillQuestion(e.target.value);
                  setTouched(true);
                }}
              />
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <Label>Answers for Blanks</Label>
                <Button type="button" onClick={addFillBlank} variant="outline" size="sm">
                  <Plus className="h-4 w-4 mr-1" /> Add Answer
                </Button>
              </div>
              {fillBlanks.map((blank, index) => (
                <div key={index} className="flex gap-2 items-center">
                  <Label className="min-w-16">Blank {index + 1}:</Label>
                  <Input
                    placeholder="Enter answer"
                    value={blank}
                    onChange={(e) => updateFillBlank(index, e.target.value)}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeFillBlank(index)}
                    disabled={fillBlanks.length <= 1}
                  >
                    <Trash className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        );

      default:
        return <div>Select a question type to continue</div>;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onCancel}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Edit Question
            <Badge variant="outline" className="capitalize">
              {questionType.replace('_', ' ')}
            </Badge>
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Question Type</Label>
              <Select value={questionType} onValueChange={(value) => {
                setQuestionType(value);
                setTouched(true);
              }}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="multiple_choice">Multiple Choice</SelectItem>
                  <SelectItem value="matching">Matching Pairs</SelectItem>
                  <SelectItem value="true_false">True/False</SelectItem>
                  <SelectItem value="fill_blank">Fill in the Blank</SelectItem>
                  <SelectItem value="puzzle">Puzzle/Riddle</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Difficulty</Label>
              <Select value={difficulty} onValueChange={(value) => {
                setDifficulty(value);
                setTouched(true);
              }}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="easy">Easy</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="hard">Hard</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {questionType !== 'fill_blank' && (
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label>Question Text</Label>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={getSuggestions}
                  disabled={loadingSuggestions || !questionText.trim()}
                >
                  {loadingSuggestions ? (
                    <Lightbulb className="h-4 w-4 mr-1 animate-pulse" />
                  ) : (
                    <Sparkles className="h-4 w-4 mr-1" />
                  )}
                  AI Suggestions
                </Button>
              </div>
              <Textarea
                placeholder="Enter your question..."
                value={questionText}
                onChange={(e) => {
                  setQuestionText(e.target.value);
                  setTouched(true);
                }}
                className="min-h-[80px]"
              />
              {suggestions.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-sm text-muted-foreground">AI Suggestions:</Label>
                  {suggestions.map((suggestion, index) => (
                    <div
                      key={index}
                      className="p-2 border rounded cursor-pointer hover:bg-muted transition-colors"
                      onClick={() => {
                        setQuestionText(suggestion);
                        setTouched(true);
                        setSuggestions([]);
                      }}
                    >
                      <p className="text-sm">{suggestion}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {renderQuestionTypeEditor()}

          <div className="space-y-2">
            <Label>Explanation (Optional)</Label>
            <Textarea
              placeholder="Explain why this answer is correct..."
              value={explanation}
              onChange={(e) => {
                setExplanation(e.target.value);
                setTouched(true);
              }}
            />
          </div>

          <Separator />
          
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={onCancel}>
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!touched || !questionText.trim()}>
              <Save className="h-4 w-4 mr-2" />
              Save Changes
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}